import { useState, useEffect, useRef } from 'react';

const useScrollDirection = (listenElementSelector = null, threshold = 150, delay = 100) => {
  const [isVisible, setIsVisible] = useState(true);
  const [cachedIsVisible, setCachedIsVisible] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [timeoutId, setTimeoutId] = useState(null);
  const [cachedElementSelector, setCachedElementSelector] = useState(null);

  const listenElementRef = useRef(null);

  const resetVisibility = () => {
    setIsVisible(true);
  };

  useEffect(
    () => {
      if (typeof window === 'undefined') {
        return;
      }

      function isWindow(obj) {
        return obj != null && obj === obj.window;
      }

      const handleScroll = event => {
        let currentScrollY = 0;

        if (isWindow(listenElementRef.current)) {
          currentScrollY = listenElementRef.current.scrollY;
        } else if (event.target) {
          currentScrollY = event.target.scrollTop;
        }

        const scrollDifference = currentScrollY - lastScrollY;

        if (timeoutId) {
          clearTimeout(timeoutId);
        }

        // Only trigger if we've scrolled more than the threshold
        if (Math.abs(scrollDifference) > threshold) {
          // Scrolling down
          if (scrollDifference > 0) {
            setIsVisible(false);
          }
          // Scrolling up
          else {
            setIsVisible(true);
          }

          // Update the last scroll position
          setLastScrollY(currentScrollY);
        }

        // Set a new timeout to show the header after delay
        const newTimeoutId = setTimeout(() => {
          if (currentScrollY <= 0) {
            setIsVisible(true);
          }
        }, delay);

        setTimeoutId(newTimeoutId);
      };

      if (listenElementSelector) {
        const documentElement = document.querySelector(listenElementSelector);

        if (documentElement) {
          if (listenElementRef.current) {
            listenElementRef.current.removeEventListener('scroll', handleScroll);
          }

          listenElementRef.current = documentElement;
        }
      } else if (listenElementRef.current) {
        listenElementRef.current.removeEventListener('scroll', handleScroll);
        listenElementRef.current = null;
      }

      if (!listenElementRef.current) {
        listenElementRef.current = window;
      }

      listenElementRef.current.addEventListener('scroll', handleScroll, { passive: true });

      // eslint-disable-next-line consistent-return
      return () => {
        listenElementRef.current.removeEventListener('scroll', handleScroll);
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    },
    [lastScrollY, threshold, delay, timeoutId, listenElementSelector],
  );

  // For better UX and reduce unexpected hiding behaviors,
  // visibility should reinit when selector changes
  useEffect(
    () => {
      if (cachedElementSelector === listenElementSelector) {
        return;
      }

      setCachedElementSelector(listenElementSelector);
      setIsVisible(true);
    },
    [listenElementSelector],
  );

  useEffect(
    () => {
      if (cachedIsVisible === isVisible) {
        return;
      }

      setCachedIsVisible(isVisible);
    },
    [isVisible],
  );

  return { isHidden: !cachedIsVisible, resetVisibility };
};

export default useScrollDirection;
